import React, { useState, useContext } from 'react';
import './promptBar/PromptBar.css';
import { useAuth } from '../context/AuthContext.js';
import { ChatContext } from '../context/ChatContext.js';
import RapidMindAgentButton from './RapidMindAgentButton.jsx';
function PromptBar({ onSearch, onAIResponse, onSearchResults }) {
  const [query, setQuery] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Get historical data from ChatContext
  const { history } = useContext(ChatContext);

  const handleSearch = async () => {
    if (!query.trim()) return;

    setIsSearching(true);

    try {
      // First, generate AI research using RapidMind AI tool
      await handleGenerateAI();

      // Then, provide web search results
      const mockResults = [
        {
          title: `${query.trim()} - Wikipedia`,
          description: `Learn about ${query.trim()} on Wikipedia, the free encyclopedia. Comprehensive information and resources.`,
          url: `https://en.wikipedia.org/wiki/${encodeURIComponent(query.trim())}`,
          type: 'web_result'
        },
        {
          title: `${query.trim()} - Official Documentation`,
          description: `Technical documentation and guides for ${query.trim()}. Developer resources and API references.`,
          url: `https://developer.mozilla.org/search?q=${encodeURIComponent(query.trim())}`,
          type: 'web_result'
        },
        {
          title: `${query.trim()} Tutorials`,
          description: `Step-by-step tutorials and guides for ${query.trim()}. Learn how to use and implement ${query.trim()}.`,
          url: `https://www.youtube.com/results?search_query=${encodeURIComponent(query.trim())}+tutorial`,
          type: 'web_result'
        },
        {
          title: `${query.trim()} News`,
          description: `Latest news and updates about ${query.trim()}. Stay informed with current developments.`,
          url: `https://news.google.com/search?q=${encodeURIComponent(query.trim())}`,
          type: 'web_result'
        }
      ];

      const transformedResults = {
        success: true,
        results: mockResults,
        totalResults: mockResults.length,
        searchTime: 0,
        cached: false
      };

      console.log('Mock web search results:', transformedResults);

      // Pass results to parent component via callbacks
      if (onSearchResults) {
        onSearchResults(mockResults);
      }

      // Pass results to parent component
      if (onSearch) {
        onSearch(query, transformedResults);
      }
    } catch (error) {
      console.error('Search error:', error);
      // Fallback to original behavior
      if (onSearch) {
        onSearch(query);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setUploadMessage('');
  };

  const handleFileUpload = async () => {
    if (!file) {
      setUploadMessage('Please select a file to upload.');
      return;
    }
    setUploading(true);
    setUploadMessage('');
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/tools/upload', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (response.ok) {
        setUploadMessage('File uploaded successfully.');
        setFile(null);
      } else {
        const errorData = await response.json();
        setUploadMessage(`Upload failed: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      setUploadMessage(`Upload error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!query.trim()) return;

    setIsGenerating(true);

    try {
      // Mock AI response using RapidMind AI tool
      const mockResponse = `🤖 **RapidMind AI Research Result:**\n\nBased on your query "${query.trim()}", here's what I found using advanced AI analysis:\n\n**Key Insights:**\n• Comprehensive analysis of ${query.trim()} reveals multiple perspectives\n• Current trends show significant developments in this area\n• Expert recommendations include strategic implementation approaches\n\n**Actionable Recommendations:**\n1. Focus on core fundamentals first\n2. Leverage emerging technologies for optimization\n3. Monitor industry benchmarks regularly\n\n**Next Steps:**\nConsider exploring related topics and implementing best practices for optimal results.`;

      // Pass AI response to parent component via callback
      if (onAIResponse) {
        onAIResponse(mockResponse);
      }
    } catch (error) {
      console.error('AI generation error:', error);
      const errorMessage = 'Sorry, I encountered an error while generating the AI response.';
      if (onAIResponse) {
        onAIResponse(errorMessage);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="prompt-bar">
      <input
        type="text"
        placeholder="Search your documents..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        className="prompt-input"
      />
      <button onClick={handleSearch} className="search-button" disabled={!query.trim()}>
        {isSearching ? 'Searching...' : 'Search'}
      </button>
      <input type="file" onChange={handleFileChange} className="file-input" />
      <button onClick={handleFileUpload} disabled={uploading} className="upload-button">
        {uploading ? 'Uploading...' : 'Upload'}
      </button>
      {uploadMessage && <div className="upload-message">{uploadMessage}</div>}


    </div>
  );
}

export default PromptBar;
